import { Flap } from '@shared/models/Flap';
import styles from "@shared/styles/components/TabScreen.module.css"
import { H1Title } from '@shared/designSystem/Titles';
import { Text } from '@shared/designSystem/Texts';
import designSystemStyles from '@shared/styles/designSystemStyles';
import { VerticalContainer } from '@shared/designSystem/Containers';

interface TabScreenProps {
    title: string;
    subtitle?: string;
    flaps: Flap[];
    clickedScreen: string;
    setClickedScreen: (screen: string) => void;
    className?: string;
    /**
     * Optional callback fired when a locked flap is clicked. When provided,
     * locked flaps stay clickable (e.g. to show an upsell popup) instead of
     * being fully disabled.
     */
    onLockedClick?: (id: string) => void;
}

const TabScreen: React.FC<TabScreenProps> = ({ title, subtitle, flaps, clickedScreen, setClickedScreen, className }) => {
    return (
        <VerticalContainer
            backgroundColor={designSystemStyles.colorBackgroundGray3}
            className={['DSTabScreen', className].filter(Boolean).join(' ')}
            height="100%"
            width="100%"
        >
            <VerticalContainer gap={designSystemStyles.gapNone}>
                <H1Title text={title}/>
                <Text text={subtitle}/>
            </VerticalContainer>
            <div className={styles['content-container']}>
                <div className={styles['flaps-container']}>
                    {flaps.map((flap) => {
                        return (
                            <div
                                key={flap.id}
                                className={`${styles['flap']} ${clickedScreen === flap.id ? styles['clicked'] : ''} ${flap.isLocked ? styles['locked'] : ''}`}
                                onClick={() => {
                                    setClickedScreen(flap.id)
                                }}
                            >
                                {flap.label}
                            </div>
                        )}
                    )}
                </div>
                <div 
                    className={styles['inner-container']} 
                    style={{ 
                        zIndex: 1,
                        paddingLeft: designSystemStyles.paddingLg,
                        paddingRight: designSystemStyles.paddingLg,
                        paddingTop: designSystemStyles.paddingZero
                    }}
                >
                    {flaps.find(flap => flap.id === clickedScreen)?.content}
                </div>
            </div>

        </VerticalContainer>
        // <div className={styles['content-block']}>
        // </div>
    );
};

interface TabScreenWrapperProps extends TabScreenProps { 
}

const TabScreenWrapper: React.FC<TabScreenWrapperProps> = ({ title, subtitle, flaps, clickedScreen, setClickedScreen, className }) => {
    return <TabScreen title={title} subtitle={subtitle} flaps={flaps} clickedScreen={clickedScreen} setClickedScreen={setClickedScreen} className={className} />;
};

const TabScreenSleek: React.FC<TabScreenProps> = ({ flaps, clickedScreen, setClickedScreen, onLockedClick }) => {
    return (
        <div style={{ display: "flex", flexDirection: "column", width: "100%", height: "100%", minHeight: 0 }}>
            <div
                style={{
                    width: "100%",
                    boxSizing: "border-box",
                    paddingBottom: designSystemStyles.paddingSm
                }}
            >
                <div
                    role="tablist"
                    style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: designSystemStyles.gapTabGroup,
                        backgroundColor: designSystemStyles.colorTabGroupBackground,
                        borderRadius: designSystemStyles.radiusTabGroup,
                        padding: designSystemStyles.paddingTabGroup,
                        maxWidth: "100%",
                        overflowX: "auto",
                        boxSizing: "border-box"
                    }}
                >
                    {flaps.map((flap) => (
                        <SleekFlap
                            key={flap.id}
                            id={flap.id}
                            label={flap.label}
                            content={flap.content}
                            isLocked={flap.isLocked}
                            onClick={(id) => setClickedScreen(id)}
                            onLockedClick={onLockedClick}
                            isClicked={clickedScreen === flap.id}
                        />
                    ))}
                </div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, width: "100%" }}>
                {flaps.find(flap => flap.id === clickedScreen)?.content}
            </div>
        </div>
    );
};

interface SleekFlapInterface extends Flap {
    onClick?: (id: string) => void
    onLockedClick?: (id: string) => void
    isClicked?: boolean
}

const SleekFlap: React.FC<SleekFlapInterface> = ({
    id,
    label,
    isLocked,
    onClick,
    onLockedClick,
    isClicked
}) => {
    const handleClick = () => {
        if (isLocked) {
            onLockedClick && onLockedClick(id)
            return
        }
        onClick && onClick(id)
    }

    return (
        <button
            type="button"
            role="tab"
            aria-selected={isClicked}
            aria-disabled={isLocked}
            onClick={handleClick}
            disabled={isLocked && !onLockedClick}
            style={{
                border: "none",
                cursor: isLocked ? "not-allowed" : "pointer",
                backgroundColor: isClicked ? designSystemStyles.colorBackgroundWhite : "transparent",
                boxShadow: isClicked ? designSystemStyles.shadowTabActive : "none",
                borderRadius: designSystemStyles.radiusTabItem,
                padding: designSystemStyles.paddingTabItem,
                opacity: isLocked ? 0.5 : 1,
                transition: `background-color ${designSystemStyles.transitionTab}, box-shadow ${designSystemStyles.transitionTab}, color ${designSystemStyles.transitionTab}, opacity ${designSystemStyles.transitionTab}`,
                whiteSpace: "nowrap",
                display: "inline-grid"
            }}
        >
            <span
                style={{
                    gridArea: "1 / 1",
                    fontFamily: designSystemStyles.fontFamily,
                    fontSize: designSystemStyles.fontSizeSm,
                    lineHeight: designSystemStyles.lineHeightTab,
                    fontWeight: isClicked ? designSystemStyles.fontWeightTabActive : designSystemStyles.fontWeightTabInactive,
                    color: isClicked ? designSystemStyles.colorTabTextActive : designSystemStyles.colorTabTextInactive
                }}
            >
                {label}
            </span>
            {/* invisible bold twin reserves the active-state width so tabs don't shift on selection */}
            <span
                aria-hidden="true"
                style={{
                    gridArea: "1 / 1",
                    visibility: "hidden",
                    fontFamily: designSystemStyles.fontFamily,
                    fontSize: designSystemStyles.fontSizeSm,
                    lineHeight: designSystemStyles.lineHeightTab,
                    fontWeight: designSystemStyles.fontWeightTabActive
                }}
            >
                {label}
            </span>
        </button>
    )
};

export { TabScreenWrapper, TabScreenSleek };
